<template>
  <div class="reports-page">
    <div class="reports-toolbar">
      <b-form-input
        v-model="search"
        placeholder="Search"
        class="toolbar-input"
      />
      <b-form-select
        v-model="selectedFilter"
        :options="filterOptions"
        class="toolbar-select"
      />
      <vendor-date-picker
        v-model="selectedDate"
        class="toolbar-date"
        placeholder="MM/DD/YYYY"
        date-format="Y-m-d"
      />
      <b-button
        variant="outline-secondary"
        class="toolbar-export"
        :disabled="!visibleMetricCards.length"
        @click="exportReportsCsv"
      >
        Export CSV
      </b-button>
      <b-button
        variant="outline-secondary"
        class="toolbar-export"
        :disabled="!visibleMetricCards.length"
        @click="exportReportsPdf"
      >
        Export PDF
      </b-button>
    </div>

    <b-alert
      v-if="errorMessage"
      show
      variant="danger"
      class="reports-alert"
    >
      <div>{{ errorMessage }}</div>
      <b-button
        variant="outline-danger"
        size="sm"
        @click="fetchReportsSummary"
      >
        Retry
      </b-button>
    </b-alert>

    <div
      v-if="isLoading"
      class="report-state"
    >
      <b-spinner
        small
        class="mr-50"
      />
      <span>Loading report metrics...</span>
    </div>

    <div
      v-else-if="!visibleMetricCards.length"
      class="report-state"
    >
      {{ hasActiveFilters ? 'No metrics match the current filters.' : 'No report metrics available yet.' }}
    </div>

    <b-row v-else>
      <b-col
        v-for="item in visibleMetricCards"
        :key="item.key"
        lg="4"
        md="6"
        class="mb-2"
      >
        <b-card class="metric-card">
          <button
            type="button"
            class="metric-card-clickable"
            @click="openMetricReport(item.key)"
          >
            <div class="metric-content">
              <div class="metric-left">
                <h3>{{ item.title }}</h3>
                <h5 class="text-muted">Monthly Report</h5>
                <div class="metric-value">
                  {{ item.value }}
                </div>
                <div
                  class="metric-change"
                  :class="{ danger: item.trendDirection === 'down' || item.ringColor === 'red' }"
                >
                  {{ item.changeLabel }}
                </div>
              </div>

              <div
                class="metric-ring"
                :class="item.ringColor"
              >
                <span class="ring-main">{{ item.total }}</span>
                <span class="ring-sub">Total</span>
              </div>
            </div>
          </button>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BFormInput, BFormSelect, BAlert, BButton, BSpinner,
} from 'bootstrap-vue'
import VendorDatePicker from '@/components/vendor/VendorDatePicker.vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import { downloadCsv } from '@/utils/exportCsv'
import { downloadPrintPdf } from '@/utils/exportPdf'

const METRIC_ORDER = ['likes', 'dislikes', 'followers', 'clicks', 'saved', 'share', 'reports', 'ctr', 'cpc']

const formatMetricValue = (item) => {
  if (item.type === 'ctr') {
    return `${Number(item.monthly_count || 0).toFixed(2)}%`
  }
  if (item.type === 'cpc') {
    return `$${Number(item.monthly_count || 0).toFixed(2)}`
  }
  return formatCount(item.monthly_count)
}

const formatMetricTotal = (item) => {
  if (item.type === 'ctr') {
    return `${Number(item.total || 0).toFixed(2)}%`
  }
  if (item.type === 'cpc') {
    return `$${Number(item.total || 0).toFixed(2)}`
  }
  return formatCount(item.total)
}

const formatLabel = value => {
  const text = String(value || '').trim()
  if (!text) return 'Metric'

  return text.charAt(0).toUpperCase() + text.slice(1)
}

const formatCount = value => new Intl.NumberFormat('en-US', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
}).format(Number(value || 0))

const formatGrowthLabel = item => {
  const change = Number(item?.growth_percentage || 0)
  const direction = item?.trend === 'down' ? 'down' : item?.trend === 'up' ? 'up' : 'flat'

  if (direction === 'flat') {
    return '0%'
  }

  return `${direction === 'down' ? 'v' : '^'} ${Math.abs(change)}%`
}

const buildMetricCard = item => ({
  key: item.type,
  title: item.label || formatLabel(item.type),
  value: formatMetricValue(item),
  total: formatMetricTotal(item),
  ringColor: item.highlight || item.type === 'reports' ? 'red' : 'green',
  changeLabel: formatGrowthLabel(item),
  trendDirection: item.trend || 'flat',
})

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BFormInput,
    BFormSelect,
    BAlert,
    BButton,
    BSpinner,
    VendorDatePicker,
  },
  data() {
    return {
      search: '',
      selectedFilter: '',
      selectedDate: '',
      metricCards: [],
      isLoading: false,
      errorMessage: '',
    }
  },
  mounted() {
    this.fetchReportsSummary()
  },
  computed: {
    filterOptions() {
      return [
        { value: '', text: 'Select' },
        ...this.metricCards.map(item => ({
          value: item.key,
          text: item.title,
        })),
      ]
    },
    visibleMetricCards() {
      const term = this.search.trim().toLowerCase()

      return this.metricCards.filter(item => {
        const matchesFilter = !this.selectedFilter || item.key === this.selectedFilter
        const matchesSearch = !term || item.title.toLowerCase().includes(term)

        return matchesFilter && matchesSearch
      })
    },
    hasActiveFilters() {
      return Boolean(this.search.trim() || this.selectedFilter || this.selectedDate)
    },
  },
  watch: {
    selectedDate() {
      this.fetchReportsSummary()
    },
  },
  methods: {
    async fetchReportsSummary() {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const params = {}
        if (this.selectedDate) {
          params.date = this.selectedDate
        }
        const response = await vendorPanelApi.allReports(params)
        const summary = response?.data?.data?.summary || []
        const summaryByType = summary.reduce((accumulator, item) => {
          accumulator[item.type] = item
          return accumulator
        }, {})

        this.metricCards = [
          ...METRIC_ORDER.map(key => summaryByType[key]).filter(Boolean),
          ...summary.filter(item => !METRIC_ORDER.includes(item.type)),
        ].map(buildMetricCard)
      } catch (error) {
        this.metricCards = []
        this.errorMessage = error?.response?.data?.message || 'Unable to load vendor report metrics right now.'
      } finally {
        this.isLoading = false
      }
    },
    openMetricReport(metricKey) {
      this.$router.push({ name: `reports-${metricKey}` })
    },
    exportReportsCsv() {
      if (!this.visibleMetricCards.length) return
      downloadCsv('reports-summary.csv', this.visibleMetricCards, [
        { key: 'title', label: 'Metric' },
        { key: 'value', label: 'Monthly Count' },
        { key: 'total', label: 'Total' },
        { key: 'changeLabel', label: 'Growth' },
      ])
    },
    exportReportsPdf() {
      if (!this.visibleMetricCards.length) return
      downloadPrintPdf('Reports Summary', [
        { key: 'title', label: 'Metric' },
        { key: 'value', label: 'Monthly Count' },
        { key: 'total', label: 'Total' },
        { key: 'changeLabel', label: 'Growth' },
      ], this.visibleMetricCards)
    },
  },
}
</script>

<style scoped lang="scss">
.reports-page {
  .reports-toolbar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }

  .toolbar-input {
    width: 145px;
  }

  .toolbar-select {
    width: 110px;
  }

  .toolbar-date {
    width: 155px;
  }

  .toolbar-export {
    white-space: nowrap;
  }

  .reports-alert {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .report-state {
    min-height: 10rem;
    border: 1px solid #ececf3;
    border-radius: 6px;
    background: #fff;
    color: #7f7a93;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .metric-card {
    border: 1px solid #e8e8ef;
    border-radius: 6px;
    box-shadow: 0 2px 4px rgba(35, 42, 53, 0.12);

    ::v-deep .card-body {
      padding: 1rem 1rem 0.9rem;
    }
  }

  .metric-card-clickable {
    width: 100%;
    border: 0;
    background: transparent;
    padding: 0;
    text-align: inherit;
    cursor: pointer;
  }

  .metric-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
  }

  .metric-left {
    h4 {
      color: #3f3b4f;
      font-size: 1.55rem;
      margin-bottom: 0.08rem;
      font-weight: 600;
    }

    p {
      color: #8a86a0;
      font-size: 0.8rem;
      margin-bottom: 0.4rem;
    }
  }

  .metric-value {
    color: #3f3b4f;
    font-size: 2.15rem;
    line-height: 1;
    font-weight: 600;
    margin-bottom: 0.2rem;
  }

  .metric-change {
    color: #2ebf74;
    font-weight: 600;
    font-size: 0.96rem;
  }

  .metric-change.danger {
    color: #e04857;
  }

  .metric-ring {
    width: 76px;
    height: 76px;
    border-radius: 50%;
    border: 8px solid #28c76f;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-sizing: border-box;
    text-align: center;
    padding: 0;
  }

  .metric-ring.red {
    border-color: #ff4d4f;
  }

  .ring-main {
    color: #55506a;
    font-weight: 700;
    font-size: 1.45rem;
    line-height: 1.05;
    display: block;
    margin: 0;
  }

  .ring-sub {
    color: #7f7a93;
    font-size: 0.64rem;
    font-weight: 600;
    line-height: 1;
    display: block;
    margin-top: 0.08rem;
  }
}
</style>
